import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Table, TableModule } from "primeng/table";
import { v4 as uuidv4 } from "uuid";
import { FormGroup, FormsModule } from "@angular/forms";
import { ReleasedProductVariants } from "../../../core/models/shared/releasedProductVariants";
import { ReleasedProduct } from "../../../core/models/shared/releaseProduct";
import { OrderSummaryService } from "../../../core/services/order-summary.service";
import { SamplepoListingService } from "../../../core/services/samplepo-listing.service";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { CommonModule } from "@angular/common";
import { AccordionModule } from "primeng/accordion";
import { SelectChangeEvent, SelectModule } from "primeng/select";
import { ExchangeRatesDto } from "../../../core/models/shared/exchangeRateDto";
import { PurchaseOrderLine } from "../../../core/models/samplePO/details/purchaseOrderLine";
import { PanelModule } from "primeng/panel";
import { TooltipModule } from "primeng/tooltip";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";

@Component({
  selector: "jni-inventory-purchase-finished-goods-listing",
  templateUrl: "./inventory-purchase-finished-goods-listing.component.html",
  styleUrls: ["./inventory-purchase-finished-goods-listing.component.scss"],
  imports: [
    TableModule,
    CommonModule,
    AccordionModule,
    FormsModule,
    SelectModule,
    PanelModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    TooltipModule,
  ],
})
@UntilDestroy()
export class InventoryPurchaseFinishedGoodsListingComponent
  implements OnInit, OnChanges
{
  @Input() selectedLineGroup = "";
  @Input() currencyConversion!: ExchangeRatesDto;
  @Input() site = "";
  @Input() warehouse = "";
  @Input() purchaseOrderNumber = "";
  @Input() owner = "";
  @Input() parent!: FormGroup;
  @Output() rowSaveEvent = new EventEmitter<PurchaseOrderLine>();
  @Output() copyLinesEvent = new EventEmitter<PurchaseOrderLine[]>();
  @Output() deleteLinesEvent = new EventEmitter<PurchaseOrderLine[]>();
  @Output() selectedLineGroupChangeEvent = new EventEmitter<string>();

  releasedProductsList: ReleasedProduct[] = [];
  allReleasedProductsList: ReleasedProduct[] = [];
  releasedProductsVariantsList: ReleasedProductVariants[] = [];

  filteredFinishedGoodsList: PurchaseOrderLine[] = [];
  @Input() finishedGoodsList: PurchaseOrderLine[] = [];
  selectedGoods: PurchaseOrderLine[] = []; // Array to store selected items

  descriptionCharacterCond = false;

  deleteButtonStatus = false;

  dropdown = [];
  unit = ["ea", "cs"];

  netTotalPrice = "";
  netTotalPriceConverted = "";
  isoCurrencyCode = "";
  netReteAfterConversion = 0;
  netConvertToUsd = "";
  displayAddressAssignment = false;

  filteredColors: string[] = [];
  filteredSizes: string[] = [];
  filteredStyles: string[] = [];
  filteredConfig: string[] = [];

  toolTipOptions = {
    autoHide: false,
    escape: false,
    tooltipEvent: "hover" as const,
    tooltipPosition: "top" as const,
    tooltipStyleClass: "tooltipwidth",
  };

  @ViewChild("dt") table!: Table;
  @ViewChild("tableWrapper", { static: false }) tableWrapper!: ElementRef;

  constructor(
    private _orderSummaryService: OrderSummaryService,
    private _samplepoListingService: SamplepoListingService,
    private _toastr: JniToastrService
  ) {}

  ngOnInit() {
    this.getAllReleaseProduct();
    this.getAllVariants();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.lineGroupChanges(changes);
    this.finishedGoodsListChanges(changes);
    this.selectedLineGroupChanges(changes);
    this.ownerChanges(changes);
  }

  lineGroupChanges(changes: SimpleChanges) {
    if (changes["selectedLineGroup"]) {
      this.filterFinishedGoodsLines();
    }
  }

  finishedGoodsListChanges(changes: SimpleChanges) {
    if (changes["finishedGoodsList"]) {
      this.filterFinishedGoodsLines();
    }
  }

  ownerChanges(changes: SimpleChanges) {
    if (changes["owner"]) {
      this.filterAllReleasedProducts();
    }
  }

  selectedLineGroupChanges(changes: SimpleChanges) {
    if (changes["selectedLineGroup"]) {
      if (
        !changes["selectedLineGroup"].firstChange &&
        changes["selectedLineGroup"].currentValue &&
        changes["selectedLineGroup"].previousValue
      ) {
        this.selectedLineGroupChangeEvent.emit(
          changes["selectedLineGroup"].previousValue
        );
      }
    }
  }

  filterAllReleasedProducts() {
    this.releasedProductsList = this.allReleasedProductsList?.filter(
      x => x.ownerD365 == this.owner
    );
  }

  filterFinishedGoodsLines() {
    this.selectedGoods = [];
    this.filteredFinishedGoodsList = this.finishedGoodsList.filter(
      x => x.lineGroupD365 == this.selectedLineGroup
    );
    const results = this.filteredFinishedGoodsList.map(item1 => {
      const compositeKey = this.createCompositeKey(item1);
      const matchingItem = this.releasedProductsVariantsList.find(
        item2 =>
          item2.ecoResDistinctProductVariantDisplayProductNumberD365 ===
          compositeKey
      );

      return {
        ...item1,
        styleDisplayOrderD365: matchingItem?.styleDisplayOrderD365,
        colorDisplayOrderD365: matchingItem?.colorDisplayOrderD365,
        sizeGlobalDisplayOrderD365: matchingItem?.sizeGlobalDisplayOrderD365,
      };
    });
    results.sort((a, b) => {
      const itema = a.itemNumberD365?.split("-");
      const itemb = b.itemNumberD365?.split("-");

      let itemanum = 0;
      let itembnum = 0;
      if (itema && itemb) {
        itemanum = parseInt(itema[itema.length - 1]);
        itembnum = parseInt(itemb[itemb.length - 1]);
      }

      if (itemanum !== itembnum) {
        return itemanum - itembnum;
      }

      if (
        a.styleDisplayOrderD365 &&
        b.styleDisplayOrderD365 &&
        a.styleDisplayOrderD365 !== b.styleDisplayOrderD365
      ) {
        return a.styleDisplayOrderD365 - b.styleDisplayOrderD365;
      }

      if (
        a.colorDisplayOrderD365 &&
        b.colorDisplayOrderD365 &&
        a.colorDisplayOrderD365 !== b.colorDisplayOrderD365
      ) {
        return a.colorDisplayOrderD365 - b.colorDisplayOrderD365;
      }

      return (
        (a.sizeGlobalDisplayOrderD365 ?? 0) -
        (b.sizeGlobalDisplayOrderD365 ?? 0)
      );
    });

    this.filteredFinishedGoodsList = results;
    this.setLineDetails();
  }

  createCompositeKey = (obj: PurchaseOrderLine) => {
    let word = obj.itemNumberD365;
    if (obj.styleD365 !== "" && obj.styleD365) {
      word += `-${obj.styleD365}`;
    }
    if (obj.colorD365 !== "" && obj.colorD365) {
      word += `-${obj.colorD365}`;
    }
    if (obj.sizeD365 !== "" && obj.sizeD365) {
      word += `-${obj.sizeD365}`;
    }
    return word;
  };

  hideDeleteLineButtons() {
    this.deleteButtonStatus = !this.deleteButtonStatus;
  }

  setLineDetails() {
    this.filteredFinishedGoodsList.forEach(z => {
      const product = this.releasedProductsList.find(
        y => y.itemNumberD365 === z.itemNumberD365
      );
      let variants = this.releasedProductsVariantsList.filter(
        x => x.itemIdD365 == z.itemNumberD365
      );

      if (z?.colorD365) {
        variants = variants.filter(x => x.colorD365 == z?.colorD365);
      }
      if (z?.sizeD365) {
        variants = variants.filter(x => x.sizeD365 == z?.sizeD365);
      }
      if (z?.styleD365) {
        variants = variants.filter(x => x.styleD365 == z?.styleD365);
      }
      if (z?.configD365) {
        variants = variants.filter(x => x.configD365 == z?.configD365);
      }

      z.productNameD365 = product?.nameD365 ?? "";
      z.physicalAvailableD365 = variants[0]?.physicalAvailableD365;
      z.physicalInventoryD365 = variants[0]?.physicalInventoryD365;
    });
  }

  getAllReleaseProduct() {
    this._orderSummaryService
      .getAllCategory()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.allReleasedProductsList = res.data?.filter(
            x => x.productTypeD365 !== "Service" && x.poStopped === "No"
          );
          this.releasedProductsList = this.allReleasedProductsList?.filter(
            x => x.ownerD365 == this.owner
          );
          this.setCategoryNameToLines();
          this.setLineDetails();
        },
      });
  }

  setCategoryNameToLines() {
    this.filteredFinishedGoodsList.forEach(x => {
      x.productNameD365 =
        this.releasedProductsList.find(
          y => y.itemNumberD365 == x.itemNumberD365
        )?.nameD365 ?? "";
    });
  }

  getAllVariants() {
    this._samplepoListingService
      .getAllVariants()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.releasedProductsVariantsList = res.data?.filter(
            x => x.poStopped === "No"
          );
          this.setLineDetails();
        },
      });
  }

  onFocusOut(ev: Event, event: number, purchaseOrderLine: PurchaseOrderLine) {
    ev.preventDefault();
    setTimeout(() => {
      // Handle null, undefined, or empty values
      if (
        event === null ||
        event === undefined ||
        event.toString().trim() === ""
      ) {
        purchaseOrderLine.orderedPurchaseQuantityD365 = 0;
        return;
      }
      this.calculateQuantity(event, purchaseOrderLine);
    });
  }

  onQuantityKeyDown(event: KeyboardEvent, purchaseOrderLine: PurchaseOrderLine) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    // If backspace or delete is pressed and field will become empty, set to 0
    if ((event.key === 'Backspace' || event.key === 'Delete') && 
        (value.length === 1 || input.selectionStart === 0 && input.selectionEnd === value.length)) {
      event.preventDefault();
      purchaseOrderLine.orderedPurchaseQuantityD365 = 0;
      input.value = '0';
      input.select();
    }
  }

  onTextFieldKeyDown(event: KeyboardEvent, purchaseOrderLine: PurchaseOrderLine, field: string) {
    const input = event.target as HTMLInputElement | HTMLTextAreaElement;
    const value = input.value;
    
    // If backspace or delete is pressed and field will become empty, set to empty string
    if ((event.key === 'Backspace' || event.key === 'Delete') && 
        (value.length === 1 || (input.selectionStart === 0 && input.selectionEnd === value.length))) {
      event.preventDefault();
      if (field === 'lineDescriptionD365') {
        purchaseOrderLine.lineDescriptionD365 = '';
      }
      input.value = '';
    }
  }

  checkLineItems() {
    const soStoppedList = this.releasedProductsList.map(
      item => item.itemNumberD365
    );
    const filter = this.filteredFinishedGoodsList.filter(item =>
      soStoppedList.includes(item.itemNumberD365 ?? "")
    );
    if (filter.length === this.filteredFinishedGoodsList.length) {
      return { isRestricted: false, items: [] };
    } else {
      const diff = this.filteredFinishedGoodsList.filter(
        item => !filter.includes(item)
      );
      return { isRestricted: true, items: diff };
    }
  }

  calculateQuantity(event: number, purchaseOrderLine: PurchaseOrderLine) {
    //const quantity = event;
    let quantity = event.toString().replace(/[^-0-9]/g, "");
    quantity = quantity.substring(0, 5);
    if (quantity) {
      purchaseOrderLine.orderedPurchaseQuantityD365 = parseInt(quantity);
      if (purchaseOrderLine.mockSellD365) {
        purchaseOrderLine.mockSellTotalD365 =
          parseInt(quantity) * purchaseOrderLine.mockSellD365;
      }
      const netPrice = purchaseOrderLine.purchasePriceD365;
      if (netPrice) {
        //netPrice = netPrice.replace(/[^a-zA-Z0-9.]/g, '');
        const convertedNetPrice = purchaseOrderLine.netConversion;
        this.patchValueInTotalNet(
          netPrice.toString(),
          convertedNetPrice?.toString() ?? "",
          purchaseOrderLine.orderedPurchaseQuantityD365.toString(),
          purchaseOrderLine
        );
        //this.calculateGPPercentage(purchaseOrderLine);
      }
    } else {
      purchaseOrderLine.orderedPurchaseQuantityD365 = parseInt(quantity);
    }
  }

  selectText(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    input.select();
  }

  onMockSellKeyDown(event: KeyboardEvent, purchaseOrderLine: PurchaseOrderLine) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    // If backspace or delete is pressed and field will become empty, set to 0.00
    if ((event.key === 'Backspace' || event.key === 'Delete') && 
        (value.length === 1 || (input.selectionStart === 0 && input.selectionEnd === value.length))) {
      event.preventDefault();
      purchaseOrderLine.mockSellD365 = 0;
      input.value = '0.00';
      input.select();
    }
  }

  calculateMockSellTotal(
    mockSell: number,
    purchaseOrderLine: PurchaseOrderLine
  ) {
    let value = mockSell.toString().replace(/[^-0-9.]/g, "");
    const parts = value.split(".");
    if (parts.length > 2) {
      value = parts.shift() + "." + parts.join("");
    }
    const mockSellCalc = value;
    if (mockSellCalc) {
      purchaseOrderLine.mockSellD365 = parseFloat(mockSellCalc);

      if (purchaseOrderLine.orderedPurchaseQuantityD365) {
        purchaseOrderLine.mockSellTotalD365 =
          parseInt(mockSellCalc) *
          purchaseOrderLine.orderedPurchaseQuantityD365;
      }
    } else {
      purchaseOrderLine.mockSellD365 = 0;
      purchaseOrderLine.mockSellTotalD365 = 0;
    }
  }

  patchValueInTotalNet(
    net: string,
    convertedNet: string,
    lineQuantity: string,
    lineObject: PurchaseOrderLine
  ) {
    const quantity = lineQuantity;
    if (typeof quantity == "number" || quantity) {
      const netprice = parseFloat(net) * parseInt(quantity);
      this.netTotalPrice = netprice.toString();
      lineObject.lineAmountD365 = parseFloat(this.netTotalPrice);
      const netPriceCon =
        parseFloat(convertedNet == "" ? "0" : convertedNet) *
        parseInt(quantity);
      this.netTotalPriceConverted = netPriceCon.toString();
      lineObject.netConversionTotal = parseFloat(
        this.netTotalPriceConverted ?? "0"
      );
    }
  }

  onNetConversion(event: string, lineObject: PurchaseOrderLine) {
    let netRate = event.replace(/[^0-9.]/g, "");
    const regExp = /^(\d+(\.\d+)?)$/;
    const revenue = regExp.test(netRate) ? "valid" : "invalid";
    if (revenue === "valid") {
      netRate = netRate.substring(0, 9);
      lineObject.purchasePriceD365 = parseFloat(netRate);
      if (netRate) {
        if (this.currencyConversion) {
          if (this.currencyConversion.Rate != 0) {
            this.netReteAfterConversion =
              parseFloat(netRate) * this.currencyConversion?.Rate;
            if (this.netReteAfterConversion.toString() != "") {
              this.netConvertToUsd = this.netReteAfterConversion.toString();
              lineObject.netConversion = parseFloat(
                this.netConvertToUsd ?? "0"
              );
            }
          }
        }

        //this.calculateGPPercentage(lineObject);
        this.patchValueInTotalNet(
          netRate,
          this.netReteAfterConversion.toString(),
          lineObject.orderedPurchaseQuantityD365?.toString() ?? "",
          lineObject
        );
      }
    } else {
      lineObject.purchasePriceD365 = 0;
      lineObject.lineAmountD365 = 0;
      lineObject.netConversion = 0;
      lineObject.netConversionTotal = 0;
    }
  }

  calculateGPPercentage(lineObject: PurchaseOrderLine) {
    let sumOfSellTotal = 0;
    let sumOfNetTotal = 0;
    this.filteredFinishedGoodsList.forEach(item => {
      sumOfSellTotal += item.mockSellTotalD365 ?? 0;
      sumOfNetTotal += item.lineAmountD365 ?? 0;
    });
    const calculateGpPercentage =
      ((sumOfSellTotal - sumOfNetTotal) / sumOfSellTotal) * 100;
    lineObject.mockGPPercentD365 = calculateGpPercentage;
    // this.filteredFinishedGoodsList.forEach((it) => {
    //   it.mockGPPercentD365 = calculateGpPercentage;
    //   this.onRowEditSave(it);
    // });
    this.parent.patchValue({
      mockGPercentage: calculateGpPercentage.toFixed(2),
    });
  }

  getCurrencyConvertionCode(currency: string) {
    if (currency) {
      return currency;
    } else {
      return "USD";
    }
  }

  addNewService() {
    if (!this.selectedLineGroup) {
      this._toastr.error("Error", "Select a line group first");
      return;
    }
    this.cancelAllEditRow();
    const object: PurchaseOrderLine = {
      id: 0,
      lineRecIdD365: null,
      siteD365: this.site,
      warehouseD365: this.warehouse,
      portalKeyD365: uuidv4(),
      parentD365: true,
      lineGroupD365: this.selectedLineGroup,
      purchaseOrderNumberD365: this.purchaseOrderNumber,
      jobNameD365: this.parent.get("jobName")?.value,
      wMSLocationD365: this.parent.get("location")?.value,
      purchaseUnitSymbolD365: "ea",
      orderedPurchaseQuantityD365: 0,
      mockSellD365: 0,
      mockSellTotalD365: 0,
      lineAmountD365: 0,
      purchasePriceD365: 0,
    } as PurchaseOrderLine;
    this.filteredFinishedGoodsList.push(object);
    this.finishedGoodsList.push(object);
    this.table.initRowEdit(this.table.value[this.table.value?.length - 1]);
    this.scrollToBottom();
  }

  cancelAllEditRow() {
    this.table.value.forEach(x => {
      this.table.cancelRowEdit(x);
    });
  }

  itemSelected(
    itemNumber: SelectChangeEvent,
    purchaseOrderLine: PurchaseOrderLine
  ) {
    const itemNumberValue = itemNumber.value as string;
    this.filteredColors = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumberValue && x.colorD365)
          .sort((a, b) => a.colorDisplayOrderD365 - b.colorDisplayOrderD365)
          .map(x => x.colorD365)
      ),
    ];
    this.filteredSizes = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumberValue && x.sizeD365)
          .sort(
            (a, b) =>
              a.sizeGlobalDisplayOrderD365 - b.sizeGlobalDisplayOrderD365
          )
          .map(x => x.sizeD365)
      ),
    ];
    this.filteredConfig = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumberValue && x.configD365)
          .sort((a, b) => a.configDisplayOrderD365 - b.configDisplayOrderD365)
          .map(x => x.configD365)
      ),
    ];
    this.filteredStyles = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumberValue && x.styleD365)
          .sort((a, b) => a.styleDisplayOrderD365 - b.styleDisplayOrderD365)
          .map(x => x.styleD365)
      ),
    ];

    const selectedProduct = this.releasedProductsList.find(
      x => x.itemNumberD365 == itemNumberValue
    );
    purchaseOrderLine.productNameD365 = selectedProduct?.nameD365 ?? "";
    purchaseOrderLine.mockSellD365 = 0;
    purchaseOrderLine.mockSellTotalD365 = 0;
    purchaseOrderLine.purchasePriceD365 = 0;
    purchaseOrderLine.lineAmountD365 = 0;
    const color = this.filteredColors[0];
    const size = this.filteredSizes[0];
    const config = this.filteredConfig[0];
    const style = this.filteredStyles[0];

    purchaseOrderLine.colorD365 = color;
    purchaseOrderLine.sizeD365 = size;
    purchaseOrderLine.configD365 = config;
    purchaseOrderLine.styleD365 = style;

    let variants = this.releasedProductsVariantsList.filter(
      x => x.itemIdD365 == itemNumberValue
    );

    if (purchaseOrderLine?.colorD365) {
      variants = variants.filter(
        x => x.colorD365 == purchaseOrderLine?.colorD365
      );
    }
    if (purchaseOrderLine?.sizeD365) {
      variants = variants.filter(
        x => x.sizeD365 == purchaseOrderLine?.sizeD365
      );
    }
    if (purchaseOrderLine?.styleD365) {
      variants = variants.filter(
        x => x.styleD365 == purchaseOrderLine?.styleD365
      );
    }
    if (purchaseOrderLine?.configD365) {
      variants = variants.filter(
        x => x.configD365 == purchaseOrderLine?.configD365
      );
    }

    purchaseOrderLine.physicalAvailableD365 =
      variants[0]?.physicalAvailableD365;
    purchaseOrderLine.physicalInventoryD365 =
      variants[0]?.physicalInventoryD365;

    this.generateDescription(purchaseOrderLine);
  }

  descriptionCharacterCount(obj: string) {
    if (obj && obj?.length > 45) {
      this.descriptionCharacterCond = true;
    }
  }

  generateDescription(purchaseOrderLine: PurchaseOrderLine) {
    purchaseOrderLine.lineDescriptionD365 = `${
      purchaseOrderLine.productNameD365 || ""
    }${purchaseOrderLine.colorD365 ? "-" : ""}${
      purchaseOrderLine.colorD365 || ""
    }${purchaseOrderLine.styleD365 ? "-" : ""}${
      purchaseOrderLine.styleD365 || ""
    }${purchaseOrderLine.sizeD365 ? "-" : ""}${
      purchaseOrderLine.sizeD365 || ""
    }`;
    // this.setNetPrice(purchaseOrderLine);
  }

  setNetPrice(purchaseOrderLine: PurchaseOrderLine) {
    const itemNumber = purchaseOrderLine?.itemNumberD365;
    let variants = this.releasedProductsVariantsList.filter(
      x => x.itemIdD365 == itemNumber
    );

    if (purchaseOrderLine?.colorD365) {
      variants = variants.filter(
        x => x.colorD365 == purchaseOrderLine?.colorD365
      );
    }
    if (purchaseOrderLine?.sizeD365) {
      variants = variants.filter(
        x => x.sizeD365 == purchaseOrderLine?.sizeD365
      );
    }
    if (purchaseOrderLine?.styleD365) {
      variants = variants.filter(
        x => x.styleD365 == purchaseOrderLine?.styleD365
      );
    }
    if (purchaseOrderLine?.configD365) {
      variants = variants.filter(
        x => x.configD365 == purchaseOrderLine?.configD365
      );
    }

    this.onNetConversion(
      variants[0]?.costPriceD365?.toString() || "0",
      purchaseOrderLine
    );
  }

  setSizeAndColor(itemNumber: string, purchaseOrderLine: PurchaseOrderLine) {
    this.filteredColors = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumber)
          .sort((a, b) => a.colorDisplayOrderD365 - b.colorDisplayOrderD365)
          .map(x => x.colorD365)
      ),
    ];
    this.filteredSizes = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumber)
          .sort(
            (a, b) =>
              a.sizeGlobalDisplayOrderD365 - b.sizeGlobalDisplayOrderD365
          )
          .map(x => x.sizeD365)
      ),
    ];
    this.filteredConfig = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumber)
          .sort((a, b) => a.configDisplayOrderD365 - b.configDisplayOrderD365)
          .map(x => x.configD365)
      ),
    ];
    this.filteredStyles = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumber)
          .sort((a, b) => a.styleDisplayOrderD365 - b.styleDisplayOrderD365)
          .map(x => x.styleD365)
      ),
    ];

    let variants = this.releasedProductsVariantsList.filter(
      x => x.itemIdD365 == itemNumber
    );

    if (purchaseOrderLine?.colorD365) {
      variants = variants.filter(
        x => x.colorD365 == purchaseOrderLine?.colorD365
      );
    }
    if (purchaseOrderLine?.sizeD365) {
      variants = variants.filter(
        x => x.sizeD365 == purchaseOrderLine?.sizeD365
      );
    }
    if (purchaseOrderLine?.styleD365) {
      variants = variants.filter(
        x => x.styleD365 == purchaseOrderLine?.styleD365
      );
    }
    if (purchaseOrderLine?.configD365) {
      variants = variants.filter(
        x => x.configD365 == purchaseOrderLine?.configD365
      );
    }

    purchaseOrderLine.physicalAvailableD365 =
      variants[0]?.physicalAvailableD365;
    purchaseOrderLine.physicalInventoryD365 =
      variants[0]?.physicalInventoryD365;
  }

  onEditRow(portalKey: string, purchaseOrderLine: PurchaseOrderLine) {
    this.cancelAllEditRow();
    const row = this.table.value.filter(x => x.portalKeyD365 === portalKey);
    if (row.length > 0) {
      this.table.initRowEdit(row[0]);
    }

    this.setSizeAndColor(
      purchaseOrderLine.itemNumberD365 ?? "",
      purchaseOrderLine
    );
  }

  onRowEditSave(purchaseOrderLine: PurchaseOrderLine) {
    this.rowSaveEvent.emit(purchaseOrderLine);
  }

  onCopyLines() {
    const selectedPoLines = [...this.selectedGoods];
    const portalOnlyLines = this.selectedGoods.filter(x => !x.id);
    selectedPoLines.push(...portalOnlyLines);
    if (selectedPoLines.length === 0) {
      this._toastr.error("Error", "No lines selected");
      return;
    }
    this.selectedGoods = [];
    this.copyLinesEvent.emit(selectedPoLines);
  }

  onRowDelete() {
    const selectedPoLines = [...this.selectedGoods];
    const selectedLinesIdNotInPortal = selectedPoLines
      .filter(x => !x.id && x.portalKeyD365)
      .map(x => x.portalKeyD365);

    this.filteredFinishedGoodsList = this.filteredFinishedGoodsList.filter(
      x => !selectedLinesIdNotInPortal.includes(x.portalKeyD365)
    );

    this.selectedGoods = [];
    this.deleteLinesEvent.emit(selectedPoLines);
  }

  scrollToBottom(): void {
    setTimeout(() => {
      this.table.scrollTo({
        top: this.table.wrapperViewChild?.nativeElement.scrollHeight,
      });
    });
  }

  refreshGrid() {
    // this.gridRefresh.emit();
  }

  onSelectClosed() {
    // use a timeout to ensure p-table finishes internal logic
    setTimeout(() => {
      const active = document.activeElement as HTMLElement;
      const next = active
        ?.closest("td")
        ?.nextElementSibling?.querySelector("[pinputtext], textarea, input");
      if (next) (next as HTMLElement).focus();
    }, 100);
  }
}
